'use client';
import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Distributor, getEBTierLabel, getRankDotColor } from '@/data/mockData';
import RankBadge from '@/components/ui/RankBadge';
import { CheckCircle, XCircle, AlertCircle, Users } from 'lucide-react';

interface Props {
  distributor: Distributor;
  allDistributors: Record<string, Distributor>;
  gsvTrend: { month: string; gsv: number; activeCount: number; commissions: number }[];
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 shadow-xl">
      <p className="text-xs font-semibold text-zinc-300 mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={`tooltip-${p.dataKey}`} className="text-xs font-mono" style={{ color: p.color }}>
          {p.name}: {p.dataKey === 'gsv' ? `${(p.value/1000).toFixed(1)}K` : p.value}
        </p>
      ))}
    </div>
  );
};

export default function CenterPerformancePanel({ distributor, allDistributors, gsvTrend }: Props) {
  const directDownline = distributor.children.map(id => allDistributors[id]).filter(Boolean);
  const ebTier = getEBTierLabel(distributor.gsv);
  const isExecutive = ['Executive', 'Provisional Executive', 'Emerald', 'Diamond', 'Blue Diamond', 'Presidential Director'].includes(distributor.rank);

  const qualStageMap: Record<string, { label: string; color: string; pct: number }> = {
    None: { label: 'Not Qualifying', color: 'text-zinc-500', pct: 0 },
    LOI: { label: 'LOI — Month 1', color: 'text-amber-400', pct: 33 },
    Q1: { label: 'Q1 — Month 2', color: 'text-blue-400', pct: 66 },
    Q2: { label: 'Q2 — Month 3', color: 'text-emerald-400', pct: 99 },
    Qualified: { label: 'Executive', color: 'text-purple-400', pct: 100 },
  };
  const qs = qualStageMap[distributor.qualStage];

  return (
    <div className="p-4 space-y-4">
      {/* Distributor Header */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-violet-600/20 border border-violet-500/20 flex items-center justify-center text-sm font-bold text-violet-300">
              {distributor.name.split(' ').map(n => n[0]).join('')}
            </div>
            <div>
              <h2 className="text-base font-bold text-zinc-100">{distributor.name}</h2>
              <div className="flex items-center gap-2 mt-0.5">
                <RankBadge rank={distributor.rank} size="xs" />
                <span className="text-[10px] font-mono text-zinc-600">{distributor.id}</span>
                <span className={`flex items-center gap-1 text-[10px] font-semibold ${distributor.active ? 'text-emerald-400' : 'text-red-400'}`}>
                  {distributor.active ? <CheckCircle size={10} /> : <XCircle size={10} />}
                  {distributor.active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className="text-xs text-zinc-600">EB Tier</span>
            <span className="text-lg font-bold text-amber-400 font-mono">{ebTier}</span>
          </div>
        </div>

        {/* Key Stats */}
        <div className="grid grid-cols-4 gap-2 mt-3">
          {[
            { label: 'PSV', value: distributor.psv.toString(), ok: distributor.psv >= 100 },
            { label: 'GSV', value: `${(distributor.gsv/1000).toFixed(1)}K`, ok: true },
            { label: 'G1 Execs', value: distributor.g1BreakawayCount.toString(), ok: true },
            { label: 'ADR', value: distributor.adrActive ? 'Active' : 'Inactive', ok: distributor.adrActive },
          ].map(stat => (
            <div key={`stat-${stat.label}`} className="bg-zinc-800/50 rounded-lg p-2 text-center">
              <p className="text-[9px] uppercase tracking-wider text-zinc-600 font-semibold">{stat.label}</p>
              <p className={`text-sm font-bold font-mono mt-0.5 ${stat.ok ? 'text-zinc-100' : 'text-red-400'}`}>{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Qual Progress */}
        {distributor.qualStage !== 'None' && (
          <div className="mt-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] text-zinc-500 font-semibold uppercase tracking-wider">Exec Qualification</span>
              <span className={`text-[10px] font-semibold ${qs.color}`}>{qs.label}</span>
            </div>
            <div className="w-full bg-zinc-800 rounded-full h-1.5">
              <div className="h-1.5 rounded-full bg-gradient-to-r from-violet-600 to-violet-400 transition-all duration-500" style={{ width: `${qs.pct}%` }} />
            </div>
            <div className="flex justify-between mt-1">
              {['LOI\n1K GSV', 'Q1\n1.5K GSV', 'Q2\n2K GSV', 'Exec'].map((stage, i) => (
                <span key={`qs-stage-${i}`} className="text-[8px] text-zinc-700 text-center whitespace-pre-line">{stage}</span>
              ))}
            </div>
          </div>
        )}

        {distributor.pexecMonthsRemaining !== null && (
          <div className="mt-2 flex items-center gap-2 bg-orange-950/30 border border-orange-500/20 rounded-lg px-3 py-2">
            <AlertCircle size={13} className="text-orange-400 flex-shrink-0" />
            <span className="text-xs text-orange-300">PEXEC — {distributor.pexecMonthsRemaining} month(s) remaining before lapse</span>
          </div>
        )}
      </div>

      {/* GSV Trend Chart */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Network GSV Trend — 12 Months</h3>
          <span className="text-[10px] text-zinc-600">Last updated Apr 2026</span>
        </div>
        <ResponsiveContainer width="100%" height={140}>
          <AreaChart data={gsvTrend} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="gsvGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#7C3AED" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#7C3AED" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 9, fill: '#71717a' }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1000).toFixed(0)}K`} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="gsv" name="GSV" stroke="#7C3AED" strokeWidth={2} fill="url(#gsvGrad)" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Direct Downline Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Users size={13} className="text-zinc-500" />
            <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Direct Downline</span>
          </div>
          <span className="text-[10px] text-zinc-600 font-mono">{directDownline.length} members</span>
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-zinc-800">
              {['Name', 'Rank', 'PSV', 'GSV', 'Status'].map(h => (
                <th key={`th-${h}`} className="text-left text-[9px] font-semibold uppercase tracking-wider text-zinc-600 px-3 py-2">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {directDownline.map(child => (
              <tr key={`child-${child.id}`} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors">
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${getRankDotColor(child.rank)}`} />
                    <span className="text-xs text-zinc-200 font-medium truncate max-w-[100px]">{child.name}</span>
                  </div>
                </td>
                <td className="px-3 py-2">
                  <RankBadge rank={child.rank} size="xs" />
                </td>
                <td className="px-3 py-2">
                  <span className={`text-xs font-mono font-semibold ${child.psv >= 100 ? 'text-zinc-300' : 'text-red-400'}`}>{child.psv}</span>
                </td>
                <td className="px-3 py-2">
                  <span className="text-xs font-mono text-zinc-300">{(child.gsv/1000).toFixed(1)}K</span>
                </td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] font-semibold ${child.active ? 'text-emerald-400' : 'text-red-400'}`}>
                    {child.active ? '● Active' : '○ Inactive'}
                  </span>
                </td>
              </tr>
            ))}
            {directDownline.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-xs text-zinc-600">No direct downline members</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}